import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ClientEditDrawer } from "./ClientsPage";
import {
  Button,
  Input,
  Select,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ConfirmDialog,
  useToast,
  ScoreMiniGauge,
  ScoreDetailsDialog,
  mockScore,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import {
  listLoans,
  getLoansStats,
  deleteLoan,
  markLoanAsLoss,
  unmarkLoanAsLoss,
  type LoanResponse,
  type LoansStats,
  type LoanTab,
} from "../../services/loans.service";
import {
  createAcordo,
  listAcordos,
  type AcordoResponse,
  type CreateAcordoData,
} from "../../services/acordos.service";
import {
  getClientScore,
  getClientScoresBatch,
  type ClientScoreResponse,
} from "../../services/clients.service";
import { BlockedBadge } from "../../components/BlockedBadge";

function formatCurrency(value: string | number | null | undefined): string {
  if (value == null) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function formatCpf(cpf: string | null | undefined): string {
  if (!cpf) return "";
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/** Palette deterministica pro avatar circular do cliente (por letra). */
function avatarColor(seed: string): string {
  const palette = ["#EF4444", "#F97316", "#EAB308", "#22C55E", "#14B8A6", "#3B82F6", "#8B5CF6", "#EC4899", "#64748B"];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function daysAgo(dateStr: string | null | undefined, t: TFunction): string {
  if (!dateStr) return "";
  const d = new Date(dateStr.length === 10 ? dateStr + "T00:00:00" : dateStr);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return t("loans.row_days_ago_today");
  return t("loans.row_days_ago", { count: days });
}

function frequenciaLabel(freq: string, t: TFunction): string {
  const map: Record<string, string> = {
    diario: t("loans.freq_daily"),
    semanal: t("loans.freq_weekly"),
    mensal: t("loans.freq_monthly"),
    quinzenal: t("loans.freq_biweekly"),
    personalizado: t("loans.freq_custom"),
  };
  return map[freq] || freq;
}

/**
 * Descreve o status da proxima parcela em relacao a hoje, com cor +
 * texto. Usado logo abaixo do "Parcela X de Y" na coluna STATUS.
 */
function nextDueBadge(loan: LoanResponse, t: TFunction): { text: string; color: string } {
  if (loan.status === "finalizado") {
    return { text: t("loans.row_finished"), color: "text-muted-foreground" };
  }
  const dias = loan.diasAtraso ?? 0;
  if (dias > 0) {
    const overdueLabel = t("loans.row_overdue_days", { count: dias });
    return { text: `${formatDate(loan.proximaParcelaVencimento)} (${overdueLabel})`, color: "text-red-600" };
  }
  const venc = loan.proximaParcelaVencimento;
  if (!venc) return { text: "-", color: "text-muted-foreground" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(venc + "T00:00:00");
  const diffDays = Math.round((dueDate.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return { text: `${formatDate(venc)} (${t("loans.row_today")})`, color: "text-orange-600" };
  if (diffDays === 1) return { text: `${formatDate(venc)} (${t("loans.row_tomorrow")})`, color: "text-yellow-600" };
  return { text: `${formatDate(venc)} (${t("loans.row_in_days", { count: diffDays })})`, color: "text-green-600" };
}

interface TabDef {
  id: LoanTab;
  label: string;
  count?: number;
  tone?: "default" | "danger";
}

export function LoansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission, user } = useAuth();
  const { toast } = useToast();

  /*
   * Aceita ?tab=vencendo_hoje (ou qualquer outra tab valida) da URL —
   * usado pelo botao "Ver clientes pendentes" da dashboard home. Se
   * invalido/ausente, volta pra "em_dia".
   */
  const VALID_TABS: LoanTab[] = [
    "todos", "em_dia", "atrasado", "vencendo_hoje", "inativo", "perda", "acordo",
  ];
  const initialTab = ((): LoanTab => {
    const t = searchParams.get("tab") as LoanTab | null;
    return t && VALID_TABS.includes(t) ? t : "em_dia";
  })();
  const [tab, setTab] = useState<LoanTab>(initialTab);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [loans, setLoans] = useState<LoanResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState<LoansStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Confirmacoes
  const [deleteTarget, setDeleteTarget] = useState<LoanResponse | null>(null);
  const [lossTarget, setLossTarget] = useState<LoanResponse | null>(null);

  // Edicao inline de cliente (reusa ClientEditDrawer da ClientsPage)
  const [editClientId, setEditClientId] = useState<string | null>(null);

  // Modal fazer acordo
  const [acordoTarget, setAcordoTarget] = useState<LoanResponse | null>(null);
  const [acordoHistoryTarget, setAcordoHistoryTarget] = useState<LoanResponse | null>(null);

  // Modal detalhes do score (mesmo padrao da PipelinePage)
  const [scoreTarget, setScoreTarget] = useState<LoanResponse | null>(null);
  const [scoreDetails, setScoreDetails] = useState<ClientScoreResponse | null>(null);
  const [scoreLoading, setScoreLoading] = useState(false);

  // Score real por cliente da lista (batch, evita N+1). key = clienteId.
  const [scoreByClient, setScoreByClient] = useState<Record<string, number>>({});

  const canDelete = hasPermission("emprestimos:excluir");
  // So master/admin conseguem reverter uma perda — matches o requireLevel do backend.
  const canReactivate = user?.nivel === "master" || user?.nivel === "admin";

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listLoans({
        tab,
        search: search || undefined,
        page,
        limit: pageSize,
      });
      setLoans(result.data);
      setTotal(result.meta.total);

      // Batch scores dos clientes da pagina — evita mockScore mostrando 0
      // falso e nao gera N requests.
      const ids = Array.from(new Set(result.data.map((l) => l.clienteId).filter(Boolean)));
      if (ids.length > 0) {
        try {
          const scores = await getClientScoresBatch(ids);
          setScoreByClient((prev) => ({ ...prev, ...scores }));
        } catch {
          // silencioso — fallback pro mockScore na render
        }
      }
    } catch {
      toast({ variant: "error", title: t("loans.toast_load_error") });
    } finally {
      setLoading(false);
    }
  }, [tab, search, page, pageSize, toast, t]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const s = await getLoansStats({ search: search || undefined });
      setStats(s);
    } catch {
      // silencioso
    } finally {
      setStatsLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { setPage(1); }, [tab, search]);

  useEffect(() => {
    if (!scoreTarget) return;
    let cancelled = false;
    setScoreDetails(null);
    setScoreLoading(true);
    getClientScore(scoreTarget.clienteId)
      .then((res) => {
        if (cancelled) return;
        setScoreDetails(res);
        setScoreByClient((prev) => ({ ...prev, [scoreTarget.clienteId]: res.score }));
      })
      .catch(() => {
        // silencioso — cai no mock via ScoreDetailsDialog
      })
      .finally(() => { if (!cancelled) setScoreLoading(false); });
    return () => { cancelled = true; };
  }, [scoreTarget]);

  const tabs: TabDef[] = useMemo(() => {
    return [
      { id: "em_dia", label: t("loans.tab_on_time"), count: stats?.emDia },
      { id: "atrasado", label: t("loans.tab_overdue"), count: stats?.emAtraso, tone: "danger" },
      { id: "acordo", label: t("loans.tab_in_agreement"), count: stats?.emAcordo },
      { id: "vencendo_hoje", label: t("loans.tab_due_today"), count: stats?.parcelasVencendoHoje },
      { id: "inativo", label: t("loans.tab_inactive"), count: stats?.inativos, tone: "danger" },
      { id: "perda", label: t("loans.tab_loss"), count: stats?.perdas, tone: "danger" },
    ];
  }, [stats, t]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLoan(deleteTarget.id);
      toast({ variant: "success", title: t("loans.toast_delete_success") });
      setDeleteTarget(null);
      fetchList();
      fetchStats();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("loans.toast_delete_error") });
    }
  };

  const confirmLoss = async (motivo: string) => {
    if (!lossTarget) return;
    try {
      await markLoanAsLoss(lossTarget.id, motivo);
      toast({ variant: "success", title: t("loans.toast_loss_success") });
      setLossTarget(null);
      fetchList();
      fetchStats();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("loans.toast_loss_error") });
    }
  };

  // Reativar (undo perda) — so admin/master.
  const reactivate = async (loan: LoanResponse) => {
    if (!confirm(t("loans.reactivate_confirm", { name: loan.clienteNome || t("loans.default_client_name") }))) return;
    try {
      await unmarkLoanAsLoss(loan.id);
      toast({ variant: "success", title: t("loans.toast_reactivate_success") });
      fetchList();
      fetchStats();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("loans.toast_reactivate_error") });
    }
  };

  const openAcordo = (loan: LoanResponse) => {
    setAcordoTarget(loan);
  };

  const confirmAcordo = async (data: CreateAcordoData) => {
    if (!acordoTarget) return;
    try {
      await createAcordo(acordoTarget.id, data);
      toast({ variant: "success", title: t("loans.toast_acordo_success") });
      setAcordoTarget(null);
      fetchList();
      fetchStats();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("loans.toast_acordo_error") });
    }
  };

  const openClientInfo = (loan: LoanResponse) => {
    setEditClientId(loan.clienteId);
  };

  const showEmpty = !loading && loans.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("nav.loans_active")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("loans.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <DownloadIcon />
            {t("loans.export")}
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("loans.kpi_total_active")}
          value={statsLoading ? "—" : formatCurrency(stats?.valorTotalAtivo)}
          hint={statsLoading ? "" : t("loans.kpi_total_active_hint", { count: stats?.contratosAtivos ?? 0 })}
          icon={<MoneyIcon />}
          tone="primary"
        />
        <KpiCard
          label={t("loans.kpi_due_today")}
          value={statsLoading ? "—" : formatCurrency(stats?.diariasAReceberHoje)}
          hint={statsLoading ? "" : t("loans.kpi_due_today_hint", { count: stats?.parcelasVencendoHoje ?? 0 })}
          icon={<CalendarIcon />}
          tone="orange"
        />
        <KpiCard
          label={t("loans.kpi_on_time")}
          value={statsLoading ? "—" : `${stats?.emDia ?? 0}${stats?.contratosAtivos ? ` (${Math.round(((stats?.emDia ?? 0) / stats.contratosAtivos) * 100)}%)` : ""}`}
          hint={t("loans.kpi_on_time_hint")}
          icon={<CheckIcon />}
          tone="green"
        />
        <KpiCard
          label={t("loans.kpi_overdue")}
          value={statsLoading ? "—" : `${stats?.emAtraso ?? 0}${stats?.contratosAtivos ? ` (${Math.round(((stats?.emAtraso ?? 0) / stats.contratosAtivos) * 100)}%)` : ""}`}
          hint={t("loans.kpi_overdue_hint")}
          icon={<WarningIcon />}
          tone="red"
        />
      </div>

      {/* Card com Tabs + Busca + Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
            <div className="flex flex-wrap items-center gap-2">
              {tabs.map((tabDef) => {
                const isActive = tab === tabDef.id;
                const activeClass =
                  tabDef.tone === "danger"
                    ? "bg-red-600 text-white"
                    : "bg-primary-600 text-white";
                const inactiveClass = "bg-muted text-foreground hover:bg-muted/70";
                return (
                  <button
                    key={tabDef.id}
                    onClick={() => setTab(tabDef.id)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${isActive ? activeClass : inactiveClass}`}
                  >
                    {tabDef.label}
                    {tabDef.count != null && ` (${tabDef.count})`}
                  </button>
                );
              })}
            </div>
            <div className="w-full sm:w-64">
              <Input
                placeholder={t("loans.search_loan_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">{t("loans.col_client")}</th>
                  <th className="px-4 py-3">{t("loans.col_campaign")}</th>
                  <th className="px-4 py-3">{t("loans.col_loan_date")}</th>
                  <th className="px-4 py-3">{t("loans.col_value_installments")}</th>
                  <th className="px-4 py-3">{t("loans.col_loan_status")}</th>
                  <th className="px-4 py-3 text-center">{t("loans.col_score")}</th>
                  <th className="px-4 py-3 text-center">{t("loans.col_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-muted-foreground">
                      {t("loans.row_loading")}
                    </td>
                  </tr>
                )}
                {showEmpty && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-muted-foreground">
                      {t("loans.row_empty")}
                    </td>
                  </tr>
                )}
                {!loading &&
                  loans.map((loan) => {
                    const nome = loan.clienteNome || t("loans.default_client_name");
                    const badge = nextDueBadge(loan, t);
                    const totalContrato = formatCurrency(loan.valorTotal);
                    const parcelaValor = loan.proximaParcelaValor
                      ? formatCurrency(loan.proximaParcelaValor)
                      : formatCurrency(parseFloat(loan.valorTotal) / loan.numParcelas);
                    const proxNum = loan.proximaParcelaNumero ?? (loan.parcelasPagas + 1);
                    const progress = Math.min(100, (loan.parcelasPagas / loan.numParcelas) * 100);
                    const isInativo = loan.status === "finalizado";
                    return (
                      <tr key={loan.id} className="hover:bg-muted/40">
                        {/* Cliente */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: avatarColor(nome) }}
                            >
                              {initials(nome)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate font-semibold text-foreground">{nome}</p>
                                {loan.clienteBloqueado && <BlockedBadge />}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {t("loans.row_cpf", { cpf: formatCpf(loan.clienteCpf) })}
                              </p>
                              {loan.perda && (
                                <span
                                  title={loan.perdaMotivo || t("loans.badge_loss_title")}
                                  className="mt-1 inline-block rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                                >
                                  {t("loans.badge_loss")}
                                </span>
                              )}
                              {!loan.perda && (loan.acordosTotal ?? 0) > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAcordoHistoryTarget(loan);
                                  }}
                                  title={t("loans.badge_in_agreement_title")}
                                  className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white transition-colors ${
                                    loan.emAcordo
                                      ? "bg-blue-600 hover:bg-blue-700"
                                      : "bg-slate-500 hover:bg-slate-600"
                                  }`}
                                >
                                  {loan.acordosTotal}{" "}
                                  {loan.acordosTotal === 1
                                    ? t("loans.badge_agreement_count_one")
                                    : t("loans.badge_agreement_count_other")}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Campanha */}
                        <td className="px-4 py-3 align-top">
                          {loan.campanhaNome ? (
                            <div>
                              <p className="text-sm font-medium text-foreground">{loan.campanhaNome}</p>
                              {loan.campanhaCodigo && (
                                <p className="text-xs text-muted-foreground">{loan.campanhaCodigo}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        {/* Data */}
                        <td className="px-4 py-3 align-top">
                          <p className="text-foreground">{formatDate(loan.data)}</p>
                          <p className="text-xs text-muted-foreground">({daysAgo(loan.data, t)})</p>
                        </td>
                        {/* Valor / Parcelas — clique abre modal parcelas */}
                        <td className="px-4 py-3 align-top">
                          <button
                            onClick={() => navigate(`/dashboard/emprestimos/${loan.id}`)}
                            className="group text-left"
                          >
                            <p className="font-bold text-foreground group-hover:text-primary-600">
                              {formatCurrency(loan.valor)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t("loans.row_installments_x_of", { count: loan.numParcelas, value: parcelaValor })}
                            </p>
                            <p className="text-xs text-muted-foreground">{t("loans.row_total", { value: totalContrato })}</p>
                          </button>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase text-primary-700">
                            <span className="rounded bg-primary-100 px-1.5 py-0.5 dark:bg-primary-900/40">
                              PIX
                            </span>
                            <span className="text-muted-foreground">·</span>
                            <span>{frequenciaLabel(loan.frequencia, t)}</span>
                          </div>
                          <p className="mt-1 text-sm font-semibold text-foreground">
                            {isInativo
                              ? t("loans.row_paid_installments", { paid: loan.numParcelas, total: loan.numParcelas })
                              : t("loans.row_installment_of", { current: proxNum, total: loan.numParcelas })}
                          </p>
                          <p className={`text-xs font-medium ${badge.color}`}>
                            {isInativo ? t("loans.row_not_renewed") : `${parcelaValor} · ${badge.text}`}
                          </p>
                          <div className="mt-1.5 h-1 w-40 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${progress}%`,
                                backgroundColor: (loan.diasAtraso ?? 0) > 0 ? "#DC2626" : "#22C55E",
                              }}
                            />
                          </div>
                        </td>
                        {/* Score */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex justify-center">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setScoreTarget(loan); }}
                              title={t("loans.score_dialog_title")}
                              className="transition-transform hover:scale-105"
                            >
                              <ScoreMiniGauge score={scoreByClient[loan.clienteId] ?? mockScore(loan.clienteCpf)} />
                            </button>
                          </div>
                        </td>
                        {/* Ações */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap items-center justify-center gap-1.5">
                            {canDelete && (
                              <ActionTile
                                tone="danger"
                                icon={<TrashIcon />}
                                label={t("loans.action_delete")}
                                onClick={() => setDeleteTarget(loan)}
                              />
                            )}
                            {/* Acordo so faz sentido em emprestimo ativo/em cobranca.
                                Em perda bate a trava do backend ("nao permite acordo
                                em emprestimo marcado como perda") — reativar antes. */}
                            {!loan.perda && (
                              <ActionTile
                                tone="warning"
                                icon={<HandshakeIcon />}
                                label={t("loans.action_agreement")}
                                onClick={() => openAcordo(loan)}
                              />
                            )}
                            <ActionTile
                              tone="info"
                              icon={<UserIcon />}
                              label={t("loans.action_client_info")}
                              onClick={() => openClientInfo(loan)}
                            />
                            {!isInativo && !loan.perda && (
                              <ActionTile
                                tone="danger"
                                icon={<FlagIcon />}
                                label={t("loans.action_mark_loss")}
                                onClick={() => setLossTarget(loan)}
                              />
                            )}
                            {loan.perda && canReactivate && (
                              <ActionTile
                                tone="success"
                                icon={<UndoIcon />}
                                label={t("loans.action_reactivate")}
                                onClick={() => reactivate(loan)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Paginacao simples */}
          {!loading && total > pageSize && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("loans.pagination_showing", { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("loans.pagination_prev")}
                </Button>
                <span className="rounded-md bg-primary-600 px-3 py-1 text-white">{page}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page * pageSize >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("loans.pagination_next")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm delete */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={t("loans.delete_title")}
        description={deleteTarget ? t("loans.delete_description", { name: deleteTarget.clienteNome || t("loans.default_client_name"), value: formatCurrency(deleteTarget.valor) }) : ""}
        confirmLabel={t("loans.delete_confirm")}
        variant="danger"
        onConfirm={confirmDelete}
      />

      {/* Edicao inline de cliente — mesmo Drawer da ClientsPage */}
      <ClientEditDrawer
        open={!!editClientId}
        onOpenChange={(o) => { if (!o) setEditClientId(null); }}
        clientId={editClientId}
        onSaved={() => { fetchList(); fetchStats(); }}
      />

      {/* Modal de perda (com motivo) */}
      {lossTarget && (
        <LossReasonModal
          loan={lossTarget}
          onClose={() => setLossTarget(null)}
          onConfirm={confirmLoss}
        />
      )}

      {/* Modal Fazer acordo */}
      {acordoTarget && (
        <AcordoModal
          loan={acordoTarget}
          onClose={() => setAcordoTarget(null)}
          onConfirm={confirmAcordo}
        />
      )}

      {/* Modal Historico de acordos daquele emprestimo */}
      {acordoHistoryTarget && (
        <AcordosHistoryModal
          loan={acordoHistoryTarget}
          onClose={() => setAcordoHistoryTarget(null)}
        />
      )}

      {/* Modal detalhes do score — mesmo componente da PipelinePage */}
      <ScoreDetailsDialog
        open={!!scoreTarget}
        onOpenChange={(o) => { if (!o) { setScoreTarget(null); setScoreDetails(null); } }}
        cpf={scoreTarget?.clienteCpf}
        data={scoreDetails}
        loading={scoreLoading}
        headerSubtitle={scoreTarget ? `${scoreTarget.clienteNome || t("loans.default_client_name")} · ${formatCpf(scoreTarget.clienteCpf)}` : undefined}
        labels={{
          title: t("loans.score_dialog_title"),
          punctualityTitle: t("loans.score_dialog_punctuality"),
          punctualityStats: (paid, total) => t("loans.score_dialog_punctuality_stats", { paid, total }),
          historyTitle: t("loans.score_dialog_history"),
          historyEmpty: t("loans.score_dialog_history_empty"),
          historyLoans: t("loans.score_dialog_history_loans"),
          historyPaidOff: t("loans.score_dialog_history_paid_off"),
          historyPaidInstallments: t("loans.score_dialog_history_paid_installments"),
          historyOverdueInstallments: t("loans.score_dialog_history_overdue_installments"),
          historyBiggestDelay: t("loans.score_dialog_history_biggest_delay"),
          historyBiggestDelayValue: (days) => t("loans.score_dialog_history_biggest_delay_value", { count: days }),
          historyRenegotiations: t("loans.score_dialog_history_renegotiations"),
          historyPartners: t("loans.score_dialog_history_partners"),
          disclaimer: t("loans.score_dialog_disclaimer"),
        }}
      />
    </div>
  );
}

/* ============================================================
 * Sub-componentes
 * ============================================================ */

interface KpiCardProps {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone: "primary" | "orange" | "green" | "red";
}

function KpiCard({ label, value, hint, icon, tone }: KpiCardProps) {
  const bg = {
    primary: "bg-primary-50 text-primary-600 dark:bg-primary-950 dark:text-primary-300",
    orange: "bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-300",
    green: "bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-300",
    red: "bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300",
  }[tone];
  const valueColor = {
    primary: "text-primary-700 dark:text-primary-300",
    orange: "text-orange-600",
    green: "text-green-600",
    red: "text-red-600",
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className={`mt-1 text-2xl font-extrabold ${valueColor}`}>{value}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${bg}`}>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

interface ActionTileProps {
  icon: React.ReactNode;
  label: string;
  tone: "danger" | "warning" | "info" | "success";
  onClick: () => void;
}

function ActionTile({ icon, label, tone, onClick }: ActionTileProps) {
  const toneClass = {
    danger: "text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-950 dark:text-red-400",
    warning: "text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-950 dark:text-yellow-400",
    info: "text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400",
    success: "text-green-600 bg-green-50 hover:bg-green-100 dark:bg-green-950 dark:text-green-400",
  }[tone];
  return (
    <button
      title={label}
      onClick={onClick}
      className={`flex h-16 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-border/40 px-1 py-1 text-[10px] font-semibold leading-tight transition-colors ${toneClass}`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="line-clamp-2 text-center">{label}</span>
    </button>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}
function MoneyIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function WarningIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
function HandshakeIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l3-3 3 3 3-3 3 3 3-3 3 3M6 9v6l3 3 3-3 3 3 3-3V9" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
function FlagIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 21V3m0 0h11l-2 4 2 4H5" />
    </svg>
  );
}
function UndoIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14L4 9l5-5M4 9h11a4 4 0 014 4v0a4 4 0 01-4 4h-4" />
    </svg>
  );
}

/* ============================================================
 * Modal Fazer Acordo — renegociacao do saldo devedor.
 * Flexibilidade total: valorAcordado, numParcelas, frequencia, data,
 * forma. Preview live das novas parcelas.
 * ============================================================ */

const FREQ_STEP_DAYS: Record<Exclude<CreateAcordoData["frequencia"], "personalizado">, number> = {
  diario: 1,
  semanal: 7,
  quinzenal: 15,
  mensal: 30,
};

function AcordoModal({
  loan,
  onClose,
  onConfirm,
}: {
  loan: LoanResponse;
  onClose: () => void;
  onConfirm: (data: CreateAcordoData) => Promise<void>;
}) {
  const { t } = useTranslation();
  const saldoDevedor = parseFloat(loan.saldoDevedor || "0");
  // Modo do ajuste: "desconto" (default) subtrai do saldo; "acrescimo" soma.
  // Renegociacao com acrescimo eh comum quando o cliente esta bem em atraso e
  // aceita pagar juros extras pra parcelar de novo.
  const [modo, setModo] = useState<"desconto" | "acrescimo">("desconto");
  const [ajusteRs, setAjusteRs] = useState(0);
  const [numParcelas, setNumParcelas] = useState(1);
  const [frequencia, setFrequencia] = useState<CreateAcordoData["frequencia"]>("mensal");
  const [intervaloDias, setIntervaloDias] = useState(15);
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [formaPagamento, setFormaPagamento] = useState<CreateAcordoData["formaPagamento"]>("pix");
  const [observacoes, setObservacoes] = useState("");
  const [busy, setBusy] = useState(false);

  // Cap superior de acrescimo: 10x o saldo (protecao contra typo). Bate com
  // o cap do backend em create-acordo.use-case.ts.
  const acrescimoMax = Math.max(0, saldoDevedor * 9);

  // Valor acordado derivado do modo:
  //  - desconto:  saldoDevedor - ajusteRs
  //  - acrescimo: saldoDevedor + ajusteRs
  const valorAcordado =
    modo === "desconto"
      ? Math.round(Math.max(0, saldoDevedor - ajusteRs) * 100) / 100
      : Math.round((saldoDevedor + ajusteRs) * 100) / 100;

  // Snapshot do estado inicial pro prompt de alteracoes nao salvas.
  const currentSnapshot = {
    modo, ajusteRs, numParcelas, frequencia,
    intervaloDias, dataInicio, formaPagamento, observacoes,
  };
  const initialRef = useRef<typeof currentSnapshot | null>(null);
  if (initialRef.current === null) initialRef.current = currentSnapshot;
  const isDirty = JSON.stringify(currentSnapshot) !== JSON.stringify(initialRef.current);

  const ajustePct = saldoDevedor > 0 ? (ajusteRs / saldoDevedor) * 100 : 0;

  const ajusteMax = modo === "desconto" ? saldoDevedor : acrescimoMax;

  const handleValorAcordado = (v: number) => {
    const clamped = Math.max(0, Math.min(saldoDevedor * 10, v));
    if (clamped <= saldoDevedor) {
      setModo("desconto");
      setAjusteRs(Math.round((saldoDevedor - clamped) * 100) / 100);
    } else {
      setModo("acrescimo");
      setAjusteRs(Math.round((clamped - saldoDevedor) * 100) / 100);
    }
  };
  const handleAjusteRs = (v: number) => {
    const clamped = Math.max(0, Math.min(ajusteMax, v));
    setAjusteRs(clamped);
  };
  const handleAjustePct = (pct: number) => {
    const maxPct = modo === "desconto" ? 100 : 900;
    const clamped = Math.max(0, Math.min(maxPct, pct));
    handleAjusteRs(Math.round((saldoDevedor * clamped) / 100 * 100) / 100);
  };

  // Preview parcelas — resiliente ao usuario digitando a data (input date
  // dispara onChange com valor parcial tipo "2026-" ou "" enquanto edita).
  // Cap defensivo em 120 linhas: mesmo com o cap no onChange, um paste ou
  // uma versao futura desse campo nao pode travar a UI renderizando N
  // milhares de linhas por tecla digitada.
  const PREVIEW_MAX_ROWS = 120;
  const preview = (() => {
    if (valorAcordado <= 0 || numParcelas < 1) return [];
    const parts = dataInicio.split("-").map(Number);
    if (
      parts.length !== 3 ||
      parts.some((n) => !Number.isFinite(n)) ||
      parts[0] < 1900 || parts[1] < 1 || parts[1] > 12 || parts[2] < 1 || parts[2] > 31
    ) {
      return [];
    }
    const [y, m, d] = parts;
    const valorParcela = Math.round((valorAcordado / numParcelas) * 100) / 100;
    const restante = Math.round((valorAcordado - valorParcela * (numParcelas - 1)) * 100) / 100;
    const step = frequencia === "personalizado" ? intervaloDias : FREQ_STEP_DAYS[frequencia];
    const rowsToShow = Math.min(numParcelas, PREVIEW_MAX_ROWS);
    const rows: { numero: number; data: string; valor: number }[] = [];
    for (let i = 0; i < rowsToShow; i++) {
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + i * step);
      if (Number.isNaN(dt.getTime())) return [];
      rows.push({
        numero: i + 1,
        data: dt.toISOString().slice(0, 10),
        valor: i === numParcelas - 1 ? restante : valorParcela,
      });
    }
    return rows;
  })();
  const previewTruncated = numParcelas > PREVIEW_MAX_ROWS;

  const todayIso = new Date().toISOString().slice(0, 10);
  const canSubmit =
    !busy &&
    valorAcordado > 0 &&
    valorAcordado <= saldoDevedor * 10 + 0.01 &&
    numParcelas >= 1 &&
    numParcelas <= 365 &&
    dataInicio >= todayIso &&
    (frequencia !== "personalizado" || (intervaloDias >= 1 && intervaloDias <= 365));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm({
        valorAcordado,
        numParcelas,
        frequencia,
        intervaloDias: frequencia === "personalizado" ? intervaloDias : undefined,
        dataPrimeiraParcela: dataInicio,
        formaPagamento,
        observacoes: observacoes.trim() || undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      dirty={isDirty}
      onSave={async () => { await handleSubmit(); }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("loans.acordo_title", { name: loan.clienteNome || t("loans.default_client_name") })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Sumario topo */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <SummaryPill label={t("loans.acordo_balance")} value={fmtBRL(saldoDevedor)} tone="danger" />
            <SummaryPill label={t("loans.acordo_agreed_value")} value={fmtBRL(valorAcordado)} tone="primary" />
            <SummaryPill
              label={
                modo === "desconto"
                  ? (ajustePct > 0 ? t("loans.acordo_discount_pct_display", { pct: ajustePct.toFixed(1) }) : t("loans.acordo_discount"))
                  : (ajustePct > 0 ? t("loans.acordo_surcharge_pct_display", { pct: ajustePct.toFixed(1) }) : t("loans.acordo_surcharge"))
              }
              value={fmtBRL(ajusteRs)}
              tone={modo === "desconto" ? "success" : "danger"}
            />
          </div>

          {/* Toggle modo: Desconto vs Acrescimo */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setModo("desconto"); setAjusteRs(0); }}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${
                modo === "desconto"
                  ? "border-green-500 bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200"
                  : "border-border bg-background text-muted-foreground hover:bg-muted"
              }`}
            >
              − {t("loans.acordo_mode_discount")}
            </button>
            <button
              type="button"
              onClick={() => { setModo("acrescimo"); setAjusteRs(0); }}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${
                modo === "acrescimo"
                  ? "border-red-500 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200"
                  : "border-border bg-background text-muted-foreground hover:bg-muted"
              }`}
            >
              + {t("loans.acordo_mode_surcharge")}
            </button>
          </div>

          {/* Valor + ajuste (bi-directional) — mascara cents-first
              pra permitir edicao natural (nao trava so no centavo). */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label={t("loans.acordo_field_agreed_value")}
              type="text"
              inputMode="numeric"
              value={fmtBRLDecimal(valorAcordado)}
              onChange={(e) => handleValorAcordado(parseCentsFirst(e.target.value))}
            />
            <Input
              label={
                modo === "desconto"
                  ? t("loans.acordo_field_discount_rs")
                  : t("loans.acordo_field_surcharge_rs")
              }
              type="text"
              inputMode="numeric"
              value={fmtBRLDecimal(ajusteRs)}
              onChange={(e) => handleAjusteRs(parseCentsFirst(e.target.value))}
            />
            <Input
              label={
                modo === "desconto"
                  ? t("loans.acordo_field_discount_pct")
                  : t("loans.acordo_field_surcharge_pct")
              }
              type="text"
              inputMode="numeric"
              value={fmtBRLDecimal(ajustePct)}
              onChange={(e) => handleAjustePct(parseCentsFirst(e.target.value))}
            />
          </div>

          {/* Parcelas + frequencia + data */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              label={t("loans.acordo_installments")}
              type="number"
              min="1"
              max="365"
              value={numParcelas}
              onChange={(e) =>
                setNumParcelas(
                  Math.min(365, Math.max(1, parseInt(e.target.value) || 1)),
                )
              }
            />
            <Select
              label={t("loans.acordo_frequency")}
              value={frequencia}
              onChange={(e) => setFrequencia(e.target.value as CreateAcordoData["frequencia"])}
              options={[
                { value: "diario", label: t("loans.freq_daily") },
                { value: "semanal", label: t("loans.freq_weekly") },
                { value: "quinzenal", label: t("loans.freq_biweekly") },
                { value: "mensal", label: t("loans.freq_monthly") },
                { value: "personalizado", label: t("loans.freq_custom") },
              ]}
            />
            {frequencia === "personalizado" && (
              <Input
                label={t("loans.acordo_interval_days")}
                type="number"
                min="1"
                max="365"
                value={intervaloDias}
                onChange={(e) =>
                  setIntervaloDias(
                    Math.min(365, Math.max(1, parseInt(e.target.value) || 1)),
                  )
                }
              />
            )}
            <Input
              label={t("loans.acordo_first_installment")}
              type="date"
              value={dataInicio}
              min={todayIso}
              onChange={(e) => setDataInicio(e.target.value)}
            />
            <Select
              label={t("loans.acordo_payment_method")}
              value={formaPagamento}
              onChange={(e) => setFormaPagamento(e.target.value as CreateAcordoData["formaPagamento"])}
              options={[
                { value: "pix", label: t("loans.detail_pay_method_pix") },
                { value: "dinheiro", label: t("loans.detail_pay_method_cash") },
                { value: "transferencia", label: t("loans.detail_pay_method_transfer") },
              ]}
            />
          </div>

          {/* Observacoes */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("loans.acordo_observations")}
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={2}
              maxLength={800}
              placeholder={t("loans.acordo_observations_placeholder")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>

          {/* Preview parcelas */}
          {preview.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("loans.acordo_preview_title")}
              </p>
              <div className="max-h-40 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-1.5 text-left">{t("loans.acordo_col_number")}</th>
                      <th className="px-3 py-1.5 text-left">{t("loans.acordo_col_due_date")}</th>
                      <th className="px-3 py-1.5 text-right">{t("loans.acordo_col_value")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview.map((p) => (
                      <tr key={p.numero}>
                        <td className="px-3 py-1 font-semibold">{p.numero}</td>
                        <td className="px-3 py-1">{new Date(p.data + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                        <td className="px-3 py-1 text-right font-medium">{fmtBRL(p.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewTruncated && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Mostrando as primeiras {PREVIEW_MAX_ROWS} de {numParcelas} parcelas.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} loading={busy} disabled={!canSubmit}>
              {t("loans.acordo_create")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Historico de acordos daquele emprestimo. Cada linha mostra data, valor,
 * parcelas, forma de pagamento, observacoes e o status atual (Ativo,
 * Cancelado, Quitado, Quebrado). Mais recentes primeiro.
 */
function AcordosHistoryModal({
  loan,
  onClose,
}: {
  loan: LoanResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<AcordoResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    listAcordos(loan.id)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [loan.id]);

  const statusLabel = (s: AcordoResponse["status"]) => {
    switch (s) {
      case "ativo":
        return { label: t("loans.acordo_status_active"), cls: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" };
      case "quitado":
        return { label: t("loans.acordo_status_paid_off"), cls: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300" };
      case "quebrado":
        return { label: t("loans.acordo_status_broken"), cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" };
      case "cancelado":
        return { label: t("loans.acordo_status_canceled"), cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {t("loans.acordo_history_title", {
              name: loan.clienteNome || t("loans.default_client_name"),
            })}
          </DialogTitle>
        </DialogHeader>

        {items === null && !error && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </p>
        )}
        {error && (
          <p className="py-8 text-center text-sm text-red-600">{error}</p>
        )}
        {items && items.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("loans.acordo_history_empty")}
          </p>
        )}
        {items && items.length > 0 && (
          <div className="space-y-3">
            {items.map((a) => {
              const st = statusLabel(a.status);
              return (
                <div
                  key={a.id}
                  className="rounded-lg border border-border bg-card p-3"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {new Date(a.createdAt).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {fmtBRL(parseFloat(a.valorAcordado))}
                        {" · "}
                        <span className="text-sm font-medium text-muted-foreground">
                          {a.numParcelas}x · {t(`loans.freq_${a.frequencia === "diario" ? "daily" : a.frequencia === "semanal" ? "weekly" : a.frequencia === "quinzenal" ? "biweekly" : a.frequencia === "mensal" ? "monthly" : "custom"}`)}
                        </span>
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t("loans.acordo_history_first_date", {
                          date: new Date(a.dataPrimeiraParcela + "T00:00:00").toLocaleDateString("pt-BR"),
                        })}
                        {" · "}
                        {t("loans.acordo_history_original", {
                          value: fmtBRL(parseFloat(a.valorOriginal)),
                        })}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${st.cls}`}
                    >
                      {st.label}
                    </span>
                  </div>
                  {a.observacoes && (
                    <p className="whitespace-pre-wrap rounded-md bg-muted/50 px-3 py-2 text-sm text-foreground">
                      {a.observacoes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryPill({ label, value, tone }: { label: string; value: string; tone: "primary" | "success" | "danger" }) {
  const cls = {
    primary: "border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-300",
    success: "border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300",
    danger: "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
  }[tone];
  // aria-hidden + pointer-events-none + select-none: deixa claro que
  // eh so um resumo, nao editavel. Sem cursor de texto piscando.
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none select-none rounded-lg border px-3 py-2 ${cls}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

function fmtBRL(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// Helpers pra mascara cents-first (padrao brasileiro de fintech):
// cada digito digitado desloca o separador decimal pra esquerda —
// "1" -> 0,01 · "100" -> 1,00 · "12345" -> 123,45.
// Evita o problema classico do <input type="number"> onde so o
// centavo fica editavel quando o valor ja tem 2 casas decimais.
function fmtBRLDecimal(n: number): string {
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseCentsFirst(raw: string): number {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return 0;
  return parseInt(digits.slice(0, 15), 10) / 100;
}

/* ============================================================
 * Modal de "Marcar como perda" — motivo com presets + textarea livre.
 * ============================================================ */

const LOSS_PRESET_KEYS = [
  "loans.loss_preset_disappeared",
  "loans.loss_preset_death",
  "loans.loss_preset_fraud",
  "loans.loss_preset_below_cost",
  "loans.loss_preset_judicial_impossible",
  "loans.loss_preset_other",
] as const;

const OTHER_PRESET_KEY = "loans.loss_preset_other";

function LossReasonModal({
  loan,
  onClose,
  onConfirm,
}: {
  loan: LoanResponse;
  onClose: () => void;
  onConfirm: (motivo: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [presetKey, setPresetKey] = useState<string>(LOSS_PRESET_KEYS[0]);
  const [detalhe, setDetalhe] = useState("");
  const [busy, setBusy] = useState(false);

  const isOutro = presetKey === OTHER_PRESET_KEY;
  const presetLabel = t(presetKey);
  const motivoFinal = isOutro ? detalhe.trim() : `${presetLabel}${detalhe.trim() ? " — " + detalhe.trim() : ""}`;
  const canSubmit = motivoFinal.length >= 3 && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(motivoFinal);
    } finally {
      setBusy(false);
    }
  };

  const isDirty = presetKey !== LOSS_PRESET_KEYS[0] || detalhe.trim() !== "";

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      dirty={isDirty}
      onSave={async () => { await handleSubmit(); }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("loans.loss_title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("loans.loss_description_prefix")}<strong>{loan.clienteNome || t("loans.default_client_name")}</strong>{t("loans.loss_description_suffix", { value: formatCurrency(loan.valor) })}
          </p>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("loans.loss_reason_label")}
            </label>
            <div className="space-y-1.5">
              {LOSS_PRESET_KEYS.map((key) => (
                <label key={key} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
                  <input
                    type="radio"
                    name="loss-preset"
                    value={key}
                    checked={presetKey === key}
                    onChange={() => setPresetKey(key)}
                    className="accent-red-600"
                  />
                  <span>{t(key)}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {isOutro ? t("loans.loss_detail_required") : t("loans.loss_detail_optional")}
            </label>
            <textarea
              value={detalhe}
              onChange={(e) => setDetalhe(e.target.value)}
              rows={3}
              maxLength={800}
              placeholder={isOutro ? t("loans.loss_placeholder_other") : t("loans.loss_placeholder_details")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
            <Button
              onClick={handleSubmit}
              loading={busy}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("loans.loss_confirm")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
